import React, { useState, useEffect } from 'react';
import { FiKey, FiCopy, FiRefreshCw } from 'react-icons/fi';
import api from '../services/api';
import './ApiKoppeling.css';

const feedUrl = (api.defaults.baseURL || '').replace(/\/api\/?$/, '') + '/api/voorraad-feed';

export default function ApiKoppeling() {
  const [apiKeyValue, setApiKeyValue] = useState(null);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    try {
      const res = await api.get('/parameters/voorraad_feed_api_key').catch(() => ({ data: null }));
      const val = res.data?.waarde;
      setApiKeyValue(val ? String(val).trim() : null);
      setNewlyGeneratedKey(null);
    } catch {
      setApiKeyValue(null);
    } finally {
      setLoading(false);
    }
  };

  const generateKey = async () => {
    if (!window.confirm('Weet je het zeker? Een bestaande API-sleutel wordt ongeldig. Het andere programma moet dan de nieuwe sleutel gebruiken.')) return;
    setGenerating(true);
    try {
      const res = await api.post('/parameters/voorraad-feed-api-key/generate');
      const key = res.data?.api_key;
      if (key) {
        setApiKeyValue(key);
        setNewlyGeneratedKey(key);
      }
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const copyKey = () => {
    const toCopy = newlyGeneratedKey || apiKeyValue;
    if (toCopy) {
      navigator.clipboard.writeText(toCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyFeedUrl = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const maskedKey = apiKeyValue ? '••••••••••••••••' + apiKeyValue.slice(-4) : null;

  return (
    <div className="api-koppeling">
      <div className="api-koppeling-card">
        <h2 className="api-koppeling-title"><FiKey /> Voorraad-feed API</h2>
        <p className="api-koppeling-intro">
          Gebruik deze API in een ander programma (bijv. WooCommerce) om voorraad per SKU op te halen: <strong>on_hand</strong>, <strong>inbound_batches</strong> en <strong>updated_at</strong>. Laat het andere programma de feed alleen periodiek ophalen (cron elke 15–30 min), niet bij pageviews.
        </p>

        <div className="api-koppeling-section">
          <h3>Endpoint</h3>
          <div className="api-koppeling-url-row">
            <code className="api-koppeling-url">{feedUrl}</code>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copyFeedUrl}>
              <FiCopy /> {copied ? 'Gekopieerd' : 'Kopieer URL'}
            </button>
          </div>
          <p className="api-koppeling-hint">Optioneel: <code>?skus=SKU1,SKU2</code> voor alleen die SKU’s.</p>
        </div>

        <div className="api-koppeling-section">
          <h3>API-sleutel</h3>
          {loading ? (
            <p>Laden...</p>
          ) : (
            <>
              {apiKeyValue ? (
                <div className="api-koppeling-key-row">
                  <code className="api-koppeling-key-display">
                    {newlyGeneratedKey ? newlyGeneratedKey : maskedKey}
                  </code>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={copyKey}>
                    <FiCopy /> {copied ? 'Gekopieerd' : 'Kopieer sleutel'}
                  </button>
                </div>
              ) : null}
              {newlyGeneratedKey && (
                <p className="api-koppeling-warning">
                  Sla deze sleutel op in het andere programma. Bij opnieuw genereren wordt hij ongeldig.
                </p>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={generateKey}
                disabled={generating}
              >
                <FiRefreshCw className={generating ? 'spin' : ''} />
                {apiKeyValue ? 'Genereer nieuwe API-sleutel' : 'Genereer API-sleutel'}
              </button>
              {!apiKeyValue && !loading && (
                <p className="api-koppeling-hint">Zonder sleutel is het endpoint vrij toegankelijk. Genereer een sleutel en stuur die mee (header <code>X-API-Key</code> of query <code>api_key</code>) voor beveiliging.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
